"use client";

import { useMemo, useState } from "react";

type Props = {
  name?: string;
  value: string;
  onChange: (value: string) => void;
};

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const TIME_SLOTS = [
  "08:00",
  "09:00",
  "10:00",
  "11:00",
  "12:00",
  "13:00",
  "14:00",
  "15:00",
  "16:00",
  "17:00",
];

function startOfDay(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function pad(n: number) {
  return String(n).padStart(2, "0");
}

function toDateKey(d: Date) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function parseValue(value: string): { dateKey: string; time: string } | null {
  if (!value || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) return null;
  const [dateKey, timePart] = value.split("T");
  return { dateKey, time: timePart.slice(0, 5) };
}

function formatSlotLabel(hhmm: string) {
  const [hStr, m] = hhmm.split(":");
  let h = Number(hStr);
  const ampm = h >= 12 ? "PM" : "AM";
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return `${h}:${m} ${ampm}`;
}

function monthLabel(year: number, month: number) {
  return new Date(year, month, 1).toLocaleString(undefined, {
    month: "long",
    year: "numeric",
  });
}

export function PreferredDateTimePicker({
  name = "preferredAt",
  value,
  onChange,
}: Props) {
  const parsed = parseValue(value);
  const today = startOfDay(new Date());
  const initial = parsed
    ? new Date(`${parsed.dateKey}T12:00:00`)
    : today;

  const [viewYear, setViewYear] = useState(initial.getFullYear());
  const [viewMonth, setViewMonth] = useState(initial.getMonth());
  const selectedDate = parsed?.dateKey ?? "";
  const selectedTime = parsed?.time ?? "";

  const days = useMemo(() => {
    const first = new Date(viewYear, viewMonth, 1);
    const startPad = first.getDay();
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const cells: Array<{ key: string; day: number; date: Date; inMonth: boolean } | null> =
      [];

    for (let i = 0; i < startPad; i++) cells.push(null);
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(viewYear, viewMonth, day);
      cells.push({ key: toDateKey(date), day, date, inMonth: true });
    }
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [viewYear, viewMonth]);

  function shiftMonth(delta: number) {
    const next = new Date(viewYear, viewMonth + delta, 1);
    setViewYear(next.getFullYear());
    setViewMonth(next.getMonth());
  }

  function pickDate(dateKey: string) {
    const time = selectedTime || "08:00";
    onChange(`${dateKey}T${time}`);
  }

  function pickTime(time: string) {
    if (!selectedDate) return;
    onChange(`${selectedDate}T${time}`);
  }

  const summary =
    selectedDate && selectedTime
      ? `${new Date(`${selectedDate}T12:00:00`).toLocaleDateString(undefined, {
          weekday: "short",
          month: "short",
          day: "numeric",
          year: "numeric",
        })} · ${formatSlotLabel(selectedTime)}`
      : selectedDate
        ? "Pick a time below"
        : "Select a date";

  return (
    <div className="pref-datetime">
      <input type="hidden" name={name} value={value} />

      <div className="pref-datetime-summary" aria-live="polite">
        {summary}
      </div>

      <div className="pref-datetime-body">
        <div className="pref-cal">
          <div className="pref-cal-head">
            <button
              type="button"
              className="pref-cal-nav"
              aria-label="Previous month"
              onClick={() => shiftMonth(-1)}
            >
              ‹
            </button>
            <div className="pref-cal-title">{monthLabel(viewYear, viewMonth)}</div>
            <button
              type="button"
              className="pref-cal-nav"
              aria-label="Next month"
              onClick={() => shiftMonth(1)}
            >
              ›
            </button>
          </div>

          <div className="pref-cal-weekdays" aria-hidden>
            {WEEKDAYS.map((d) => (
              <span key={d}>{d}</span>
            ))}
          </div>

          <div className="pref-cal-grid" role="grid" aria-label="Choose a date">
            {days.map((cell, i) => {
              if (!cell) {
                return <span key={`e-${i}`} className="pref-cal-empty" />;
              }
              const past = cell.date < today;
              const isSelected = cell.key === selectedDate;
              const isToday = cell.key === toDateKey(today);
              return (
                <button
                  key={cell.key}
                  type="button"
                  role="gridcell"
                  disabled={past}
                  aria-selected={isSelected}
                  className={
                    "pref-cal-day" +
                    (isSelected ? " is-selected" : "") +
                    (isToday ? " is-today" : "")
                  }
                  onClick={() => pickDate(cell.key)}
                >
                  {cell.day}
                </button>
              );
            })}
          </div>
        </div>

        <div className="pref-times">
          <div className="pref-times-label">Preferred time</div>
          <div className="pref-times-grid" role="listbox" aria-label="Choose a time">
            {TIME_SLOTS.map((slot) => {
              const active = slot === selectedTime;
              return (
                <button
                  key={slot}
                  type="button"
                  role="option"
                  aria-selected={active}
                  disabled={!selectedDate}
                  className={"pref-time" + (active ? " is-selected" : "")}
                  onClick={() => pickTime(slot)}
                >
                  {formatSlotLabel(slot)}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
